import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, Building2, Plus, Pencil, Trash2, Clock, MapPin, Sparkles,
  ChevronDown, Wallet, TrendingUp, Smile,
  DollarSign, Zap, Package,
} from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import PhoneInput from "@/components/PhoneInput";
import { isSubmittablePhone, PHONE_ERROR } from "@/lib/phone";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INDUSTRIES = [
  "Retail", "Restaurant / F&B", "Salon & Wellness", "Clinic / Healthcare",
  "Services / Agency", "Manufacturing", "E-commerce", "Real estate", "Other",
];
const TEAM_SIZES = ["Just me", "2 – 5", "6 – 20", "21 – 50", "50+"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const PAYMENTS = ["Cash", "UPI", "Credit Card", "Debit Card", "Net Banking", "Wallet"];
const REVENUE_RANGES = [
  "Less than ₹1L", "₹1L – ₹5L", "₹5L – ₹10L", "₹10L – ₹25L", "₹25L – ₹50L", "₹50L+",
];
const GOALS = [
  { key: "increase-revenue",  label: "Increase Revenue",        icon: TrendingUp },
  { key: "reduce-manual",     label: "Reduce Manual Work",      icon: Zap },
  { key: "customer-exp",      label: "Improve Customer Experience", icon: Smile },
  { key: "vendor-management", label: "Manage Vendors Better",   icon: Package },
  { key: "profitability",     label: "Improve Profitability",   icon: DollarSign },
  { key: "save-time",         label: "Save Time",               icon: Clock },
  { key: "other",             label: "Other",                   icon: Sparkles },
];
const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
  "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Delhi","Chandigarh","Puducherry","Jammu & Kashmir","Ladakh","Andaman & Nicobar",
  "Dadra & Nagar Haveli and Daman & Diu","Lakshadweep",
];

const GST_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export default function BusinessSetup() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();

  const [form, setForm] = useState({
    business_name: "",
    industry: "",
    team_size: "",
    country: "India",
    currency: "INR",
    phone: "",
    email: "",
    gst_number: "",
    address: { line: "", city: "", state: "", pincode: "" },
    services: [],
    working_days: [],
    opening_time: "09:00",
    closing_time: "20:00",
    payment_methods: [],
    monthly_revenue: "",
    primary_goal: "",
  });
  const [serviceEditor, setServiceEditor] = useState(null);   // null | { index, name, duration, price }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateAddress = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  const toggleInList = (key, value) => setForm((f) => {
    const has = f[key].includes(value);
    return { ...f, [key]: has ? f[key].filter((x) => x !== value) : [...f[key], value] };
  });

  const validation = useMemo(() => {
    const errs = [];
    if (!form.business_name.trim()) errs.push("Business name is required.");
    if (!form.industry) errs.push("Business type is required.");
    if (!form.team_size) errs.push("Team size is required.");
    if (!form.phone.trim()) errs.push("Phone is required.");
    else if (!isSubmittablePhone(form.phone, { required: true })) errs.push(PHONE_ERROR);
    if (!form.email.trim()) errs.push("Email is required.");
    if (form.gst_number && !GST_RE.test(form.gst_number.trim().toUpperCase()))
      errs.push("GST number format is invalid (example: 29ABCDE1234F1Z5).");
    const a = form.address;
    if (!a.line?.trim() || !a.city?.trim() || !a.state || !a.pincode?.trim())
      errs.push("Complete business address is required.");
    if (form.services.length === 0) errs.push("Add at least one service.");
    if (form.working_days.length === 0) errs.push("Select at least one working day.");
    if (!form.opening_time || !form.closing_time)
      errs.push("Working hours are required.");
    if (form.opening_time && form.closing_time && form.closing_time <= form.opening_time)
      errs.push("Closing time must be after opening time.");
    return errs;
  }, [form]);

  const canSubmit = validation.length === 0 && !loading;

  /* ----- Services CRUD ----- */
  const openNewService = () => setServiceEditor({ index: -1, name: "", duration: 30, price: 0 });
  const openEditService = (i) => {
    const s = form.services[i];
    setServiceEditor({ index: i, name: s.service_name, duration: s.duration_minutes, price: s.price });
  };
  const saveService = () => {
    const s = serviceEditor;
    if (!s.name.trim() || !s.duration || Number(s.duration) < 5 || Number(s.price) < 0) return;
    const item = { service_name: s.name.trim(), duration_minutes: Number(s.duration), price: Number(s.price) };
    setForm((f) => {
      const list = [...f.services];
      if (s.index === -1) list.push(item); else list[s.index] = item;
      return { ...f, services: list };
    });
    setServiceEditor(null);
  };
  const deleteService = (i) => setForm((f) => ({ ...f, services: f.services.filter((_, x) => x !== i) }));

  /* ----- Submit ----- */
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (validation.length > 0) { setError(validation[0]); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        gst_number: form.gst_number ? form.gst_number.trim().toUpperCase() : null,
        email: form.email || null,
      };
      await api.post("/business/setup", payload);
      await refreshUser();
      nav("/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="Business setup"
      title="Tell Velque about your business"
      subtitle="A few details so Velque can start preparing your operations in the right context."
    >
      <form onSubmit={submit} className="space-y-7" data-testid="business-setup-form">
        {/* ---------------- Identity ---------------- */}
        <Section title="Business identity">
          <Field label="Business name">
            <div className="relative">
              <input
                data-testid="setup-business-name"
                className="lux-input pl-10"
                value={form.business_name}
                onChange={(e) => update("business_name", e.target.value)}
                required
                placeholder="Acme Salon & Spa"
              />
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nexa-mute" />
            </div>
          </Field>

          <Field label="Business type">
            <div className="grid grid-cols-3 gap-2">
              {INDUSTRIES.map((i) => (
                <ChipButton
                  key={i}
                  active={form.industry === i}
                  onClick={() => update("industry", i)}
                  testId={`setup-industry-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {i}
                </ChipButton>
              ))}
            </div>
          </Field>

          <Field label="Team size">
            <div className="grid grid-cols-5 gap-2">
              {TEAM_SIZES.map((s) => (
                <ChipButton
                  key={s}
                  active={form.team_size === s}
                  onClick={() => update("team_size", s)}
                  testId={`setup-team-${s.replace(/\s+/g, "-").toLowerCase()}`}
                >{s}</ChipButton>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <PhoneInput
                value={form.phone}
                onChange={(v) => update("phone", v)}
                testId="setup-phone"
                required
              />
            </Field>
            <Field label="Email">
              <input
                data-testid="setup-email"
                type="email"
                className="lux-input"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@business.com"
                required
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="GST Number" hint="Optional · manage later from Settings">
              <input
                data-testid="setup-gst"
                className="lux-input uppercase"
                value={form.gst_number}
                onChange={(e) => update("gst_number", e.target.value.toUpperCase())}
                placeholder="29ABCDE1234F1Z5"
                maxLength={15}
              />
            </Field>
            <Field label="Currency">
              <input
                data-testid="setup-currency"
                className="lux-input"
                value={form.currency}
                onChange={(e) => update("currency", e.target.value)}
                required
              />
            </Field>
          </div>
        </Section>

        {/* ---------------- Address ---------------- */}
        <Section title="Business address" icon={MapPin}>
          <Field label="Address line">
            <input
              data-testid="setup-address-line"
              className="lux-input"
              value={form.address.line}
              onChange={(e) => updateAddress("line", e.target.value)}
              placeholder="Building, street, area"
              required
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="City">
              <input
                data-testid="setup-address-city"
                className="lux-input"
                value={form.address.city}
                onChange={(e) => updateAddress("city", e.target.value)}
                required
              />
            </Field>
            <Field label="State">
              <div className="relative">
                <select
                  data-testid="setup-address-state"
                  className="lux-input appearance-none pr-9"
                  value={form.address.state}
                  onChange={(e) => updateAddress("state", e.target.value)}
                  required
                >
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nexa-mute pointer-events-none" />
              </div>
            </Field>
            <Field label="Pincode">
              <input
                data-testid="setup-address-pincode"
                className="lux-input"
                value={form.address.pincode}
                onChange={(e) => updateAddress("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="560001"
                inputMode="numeric"
                required
              />
            </Field>
          </div>
        </Section>

        {/* ---------------- Services ---------------- */}
        <Section
          title="Services offered"
          hint="Add at least one to enable appointments · manage more later from Settings › Services"
          right={
            <span className="text-[12px] text-nexa-mute" data-testid="setup-services-count">
              {form.services.length} added
            </span>
          }
        >
          <div className="space-y-2" data-testid="setup-services-list">
            <AnimatePresence initial={false}>
              {form.services.map((s, i) => (
                <motion.div
                  key={`${s.service_name}-${i}`}
                  layout
                  initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.25 }}
                  className="flex items-center justify-between gap-3 rounded-xl border border-nexa-line bg-white px-4 py-3"
                  data-testid={`setup-service-row-${i}`}
                >
                  <div className="min-w-0">
                    <div className="text-[14px] font-medium text-nexa-ink truncate">{s.service_name}</div>
                    <div className="text-[12.5px] text-nexa-mute mt-0.5 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {s.duration_minutes} min</span>
                      <span>₹{Number(s.price).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button" onClick={() => openEditService(i)}
                      className="lux-icon-btn !w-9 !h-9"
                      data-testid={`setup-service-edit-${i}`}
                      aria-label="Edit"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button" onClick={() => deleteService(i)}
                      className="lux-icon-btn !w-9 !h-9 hover:!text-nexa-danger"
                      data-testid={`setup-service-delete-${i}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            <button
              type="button"
              onClick={openNewService}
              className="w-full h-11 rounded-xl border border-dashed border-nexa-line bg-nexa-canvas/50 text-[13.5px] text-nexa-mute hover:text-nexa-ink hover:border-nexa-accent/60 hover:bg-nexa-accent/5 transition-colors flex items-center justify-center gap-1.5"
              data-testid="setup-service-add"
            >
              <Plus className="w-4 h-4" strokeWidth={2} /> Add service
            </button>
          </div>
        </Section>

        {/* ---------------- Working days ---------------- */}
        <Section title="Working days">
          <div className="flex flex-wrap gap-2" data-testid="setup-working-days">
            {DAYS.map((d) => (
              <ChipButton
                key={d}
                active={form.working_days.includes(d)}
                onClick={() => toggleInList("working_days", d)}
                testId={`setup-day-${d.toLowerCase()}`}
                pill
              >
                {d.slice(0, 3)}
              </ChipButton>
            ))}
          </div>
        </Section>

        {/* ---------------- Working hours ---------------- */}
        <Section title="Working hours">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Opening time">
              <input
                type="time"
                className="lux-input"
                value={form.opening_time}
                onChange={(e) => update("opening_time", e.target.value)}
                data-testid="setup-opening-time"
                required
              />
            </Field>
            <Field label="Closing time">
              <input
                type="time"
                className="lux-input"
                value={form.closing_time}
                onChange={(e) => update("closing_time", e.target.value)}
                data-testid="setup-closing-time"
                required
              />
            </Field>
          </div>
        </Section>

        {/* ---------------- Payment methods ---------------- */}
        <Section title="Payment methods you accept">
          <div className="flex flex-wrap gap-2" data-testid="setup-payment-methods">
            {PAYMENTS.map((p) => (
              <ChipButton
                key={p}
                active={form.payment_methods.includes(p)}
                onClick={() => toggleInList("payment_methods", p)}
                testId={`setup-payment-${p.toLowerCase().replace(/\s+/g, "-")}`}
                pill
              >
                <Wallet className="w-3.5 h-3.5 opacity-60" strokeWidth={1.5} /> {p}
              </ChipButton>
            ))}
          </div>
        </Section>

        {/* ---------------- Revenue ---------------- */}
        <Section title="Monthly revenue range" hint="Helps NEXA scale insights to your business size">
          <div className="grid grid-cols-3 gap-2">
            {REVENUE_RANGES.map((r) => (
              <ChipButton
                key={r}
                active={form.monthly_revenue === r}
                onClick={() => update("monthly_revenue", r)}
                testId={`setup-revenue-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {r}
              </ChipButton>
            ))}
          </div>
        </Section>

        {/* ---------------- Primary goal ---------------- */}
        <Section title="Primary business goal">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="setup-goals">
            {GOALS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => update("primary_goal", g.key)}
                className={`text-left rounded-xl border px-3.5 py-3 transition-all ${
                  form.primary_goal === g.key
                    ? "bg-[#2E7D88]/8 border-[#2E7D88]"
                    : "bg-white border-nexa-line hover:border-nexa-accent/40"
                }`}
                data-testid={`setup-goal-${g.key}`}
              >
                <g.icon className={`w-4 h-4 mb-2 ${form.primary_goal === g.key ? "text-[#234B63]" : "text-nexa-mute"}`} strokeWidth={1.5} />
                <div className={`text-[13.5px] leading-tight ${form.primary_goal === g.key ? "text-[#234B63] font-semibold" : "text-nexa-ink"}`}>
                  {g.label}
                </div>
              </button>
            ))}
          </div>
        </Section>

        {error && (
          <div data-testid="setup-error" className="text-[14.5px] text-[#EF4444] bg-[#EF4444]/8 border border-[#EF4444]/20 rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        <button
          data-testid="setup-submit-btn"
          className="lux-btn-primary w-full flex items-center justify-center"
          disabled={!canSubmit}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Finish setup — meet Velque"}
        </button>
        {validation.length > 0 && (
          <div className="text-[12.5px] text-nexa-mute text-center -mt-2" data-testid="setup-validation-hint">
            <span className="font-medium text-nexa-warning">Next up:</span> {validation[0]}
            {validation.length > 1 && (
              <span className="text-nexa-mute-2"> · {validation.length - 1} more</span>
            )}
          </div>
        )}
      </form>

      {/* Service Editor Modal */}
      <ServiceModal
        state={serviceEditor}
        onChange={setServiceEditor}
        onSave={saveService}
        onClose={() => setServiceEditor(null)}
      />
    </AuthLayout>
  );
}

/* -------------------- Small primitives (scoped to this page) -------------------- */

function Section({ title, hint, icon: Icon, right, children }) {
  return (
    <section className="space-y-2.5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            {Icon && <Icon className="w-3.5 h-3.5 text-nexa-mute" strokeWidth={1.5} />}
            <div className="text-[11px] tracking-[0.18em] uppercase text-nexa-mute font-medium">{title}</div>
          </div>
          {hint && <div className="text-[12px] text-nexa-mute-2 mt-1">{hint}</div>}
        </div>
        {right}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="lux-label">{label}</label>
      {children}
      {hint && <div className="mt-1.5 text-[11.5px] text-nexa-mute-2">{hint}</div>}
    </div>
  );
}

function ChipButton({ active, onClick, testId, pill = false, children }) {
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-1.5 text-[13.5px] px-3 ${pill ? "h-9 rounded-full" : "py-2.5 rounded-xl"} border transition-all ${
        active
          ? "bg-[#2E7D88]/8 border-[#2E7D88] text-[#234B63] font-semibold"
          : "bg-white border-nexa-line text-nexa-mute hover:text-nexa-ink"
      }`}
    >
      {children}
    </motion.button>
  );
}

function ServiceModal({ state, onChange, onSave, onClose }) {
  if (!state) return null;
  const set = (k, v) => onChange({ ...state, [k]: v });
  const isEdit = state.index !== -1;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-nexa-ink/40 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="service-editor-modal"
    >
      <motion.div
        initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="lux-card w-full max-w-md p-6 max-h-[85vh] overflow-y-auto"
      >
        <h3 className="text-[17px] font-semibold text-nexa-ink mb-4">{isEdit ? "Edit service" : "New service"}</h3>
        <div className="space-y-3">
          <Field label="Service name">
            <input
              className="lux-input"
              value={state.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Haircut, Dental Cleaning"
              autoFocus
              data-testid="service-editor-name"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Duration (minutes)">
              <input
                type="number" min={5} step={5}
                className="lux-input"
                value={state.duration}
                onChange={(e) => set("duration", e.target.value)}
                data-testid="service-editor-duration"
              />
            </Field>
            <Field label="Cost (₹)">
              <input
                type="number" min={0} step={10}
                className="lux-input"
                value={state.price}
                onChange={(e) => set("price", e.target.value)}
                data-testid="service-editor-price"
              />
            </Field>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lux-btn-secondary">Cancel</button>
          <button
            type="button"
            onClick={onSave}
            className="lux-btn-primary"
            disabled={!state.name?.trim() || !state.duration || Number(state.duration) < 5}
            data-testid="service-editor-save"
          >
            {isEdit ? "Save changes" : "Add service"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
